/**
 * @description 路由工具函数
 * 提供路由权限过滤、扁平化、获取第一个有权限路由等功能
 */
import type { RouteRecordRaw } from "vue-router";

/**
 * 扩展 RouteRecordRaw 接口，添加 children 类型定义
 */
interface RouteWithChildren extends RouteRecordRaw {
    children?: RouteWithChildren[];
}

/**
 * @description 检查路由是否有权限
 * @param route 路由对象
 * @param permissions 用户权限列表
 * @returns 是否有权限
 */
const hasPermission = (route: RouteRecordRaw, permissions: string[]): boolean => {
    if (!route.meta?.authOptions || route.meta.authOptions.length === 0) {
        return true;
    }
    return route.meta.authOptions.some(authOption => permissions.includes(authOption.value));
};

/**
 * @description 递归过滤路由树
 * @param route 路由对象
 * @param permissions 用户权限列表
 * @returns 过滤后的路由对象或 null
 */
const filterRouteTree = (route: RouteWithChildren, permissions: string[]): RouteWithChildren | null => {
    const routeHasPermission = hasPermission(route, permissions);

    if (!route.children || route.children.length === 0) {
        return routeHasPermission ? route : null;
    }

    const filteredChildren: RouteWithChildren[] = [];
    for (const child of route.children) {
        const filteredChild = filterRouteTree(child, permissions);
        if (filteredChild) {
            filteredChildren.push(filteredChild);
        }
    }

    if (filteredChildren.length === 0) {
        return null;
    }

    return {
        ...route,
        children: filteredChildren
    };
};

/**
 * @description 根据权限过滤路由数组
 * @param routes 路由数组
 * @param permissions 用户权限列表
 * @returns 过滤后的路由数组
 */
export const filterRoutes = (routes: RouteRecordRaw[], permissions: string[]): RouteRecordRaw[] => {
    const filteredRoutes: RouteRecordRaw[] = [];
    for (const route of routes) {
        const filtered = filterRouteTree(route as RouteWithChildren, permissions);
        if (filtered !== null) {
            filteredRoutes.push(filtered);
        }
    }
    return filteredRoutes;
};

/**
 * @description 扁平化路由树
 * @param routes 路由数组
 * @returns 扁平化后的路由数组
 */
export const flattenRoutes = (routes: RouteRecordRaw[]): RouteRecordRaw[] => {
    const result: RouteRecordRaw[] = [];
    const traverse = (routes: RouteRecordRaw[]) => {
        for (const route of routes) {
            result.push(route);
            if (route.children && route.children.length > 0) {
                traverse(route.children);
            }
        }
    };
    traverse(routes);
    return result;
};

/**
 * @description 获取第一个有权限的路由
 * @param routes 路由数组
 * @param permissions 用户权限列表
 * @returns 第一个有权限的路由或 null
 */
export const getFirstAccessibleRoute = (routes: RouteRecordRaw[], permissions: string[]): RouteRecordRaw | null => {
    const filteredRoutes = flattenRoutes(filterRoutes(routes, permissions));
    return filteredRoutes.length > 0 ? filteredRoutes[0]! : null;
};

/**
 * @description 获取顶层有权限的路由
 * @param routes 路由数组
 * @param permissions 用户权限列表
 * @returns 顶层有权限的路由数组
 */
export const getTopLevelRoutes = (routes: RouteRecordRaw[], permissions: string[]): RouteRecordRaw[] => {
    return filterRoutes(routes, permissions);
};
